import React, { useEffect } from "react";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  CheckSquare,
  Clock,
  TrendingUp,
  PhoneOff,
  Workflow,
  Bot,
  Plug,
  Zap,
  Inbox,
  Send,
  CalendarClock,
  PhoneCall,
  Award,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import useReveal from "../../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Pieces --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({ children = "Book Your Strategy Call", testid }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
  >
    {children}
    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
);

/* ---------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section
    data-testid="ai-hero"
    className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden"
  >
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>

    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <Link
        to="/services"
        data-testid="ai-hero-back"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53] reveal"
      >
        <ArrowLeft size={14} />
        All services
      </Link>

      <div className="mt-5 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          <Eyebrow>AI Agents &amp; Automations</Eyebrow>
          <h1 className="h1-hero mt-5 text-[#0A1628]">
            AI Agents and Automations That{" "}
            <span className="text-[#16A06A]">Turn Into Revenue.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-[620px]">
            AI agents and workflow automation that eliminate bottlenecks,
            accelerate lead response, and give your revenue team their time
            back.
          </p>

          <div className="mt-8">
            <PrimaryCTA testid="ai-hero-cta" />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            {[
              "Built and managed for you",
              "Connects to your stack",
              "Live in weeks",
            ].map((c) => (
              <div key={c} className="inline-flex items-center gap-2 text-[14px] text-slate-600">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="rounded-2xl bg-white card-soft border border-slate-200 overflow-hidden">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1518186285589-2f7649de83e0"
                alt="AI automation workflow dashboard"
                loading="eager"
                className="w-full h-[400px] lg:h-[480px] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/85 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg bg-white/95 backdrop-blur border border-slate-200 px-3 py-2 card-soft">
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
      {label}
    </div>
    <div className="text-[16px] font-bold text-[#0A1628] leading-tight">
      {value}
    </div>
  </div>
);

/* ---------------------------------- Stats --------------------------------- */

const STATS = [
  {
    title: "Answers in minutes",
    body: "A missed call or web inquiry gets a reply while the customer is still deciding who to call.",
  },
  {
    title: "Nothing sits in an inbox",
    body: "Inquiries are logged, routed and followed up without anyone having to remember.",
  },
  {
    title: "Your CRM stays current",
    body: "Job records, notes and next steps updated as the work happens, not on Friday afternoon.",
  },
  {
    title: "Built around your tools",
    body: "Runs inside the systems you already pay for, not a new one to learn.",
  },
];

const Stats = () => (
  <section data-testid="ai-stats" className="py-16 lg:py-20 bg-[#F8FAFC] border-y border-slate-200">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
        {STATS.map((s, i) => (
          <div
            key={s.title}
            className="reveal text-left lg:pl-8 lg:border-l border-slate-200 first:border-l-0 first:pl-0"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="text-[17px] lg:text-[18px] font-bold text-[#0A1628] leading-snug">
              {s.title}
            </div>
            <div className="mt-3 text-[14px] leading-relaxed text-slate-500">
              {s.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------- Problem --------------------------------- */

const PROBLEMS = [
  {
    icon: CheckSquare,
    title: "Reps Spend Only 30% of Time Selling",
    body:
      "According to Salesforce, 70% of rep time goes to admin tasks, CRM updates, and internal meetings. Gartner puts admin burden at 50%.",
  },
];

const Problem = () => (
  <section data-testid="ai-problem" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Issue</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Your Revenue Team Is{" "}
          <span className="text-[#16A06A]">Drowning in Busywork.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          While your reps wrestle with CRM updates and scheduling logistics,
          your pipeline is leaking revenue. The data tells the story.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {PROBLEMS.map((p, i) => {
          const Icon = p.icon;
          return (
            <article
              key={p.title}
              data-testid={`ai-problem-card-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={18} strokeWidth={2.4} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                {p.body}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* -------------------------------- Solution -------------------------------- */

const SOLUTIONS = [
  {
    icon: Workflow,
    title: "Workflow Automation",
    body:
      "Streamlined, traceable workflows across your entire GTM stack. From lead intake to follow-up, every step runs automatically with zero manual data entry.",
  },
  {
    icon: Bot,
    title: "AI Agents",
    body:
      "Purpose-built AI agents that handle email triage, personalized outreach, meeting scheduling, and post-call follow-ups autonomously, around the clock.",
  },
  {
    icon: Plug,
    title: "Deep Integrations",
    body:
      "Connect with the tools your team already uses. Data stays synchronized across systems with no manual exports or version-control nightmares.",
  },
  {
    icon: Zap,
    title: "Always-On Execution",
    body:
      "Your automations never sleep. Leads get processed instantly, follow-ups fire on schedule, and your pipeline keeps moving even when your team is offline.",
  },
];

const Solution = () => (
  <section data-testid="ai-solution" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Solution</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Intelligent Systems That Handle the Repetitive Work.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          We build intelligent systems that handle the repetitive work so your
          team can focus on closing deals. Core capabilities, one unified
          platform.
        </p>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SOLUTIONS.map((s, i) => {
          const Icon = s.icon;
          return (
            <article
              key={s.title}
              data-testid={`ai-solution-card-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">
                {s.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">
                {s.body}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* --------------------------------- Agents --------------------------------- */

const AGENTS = [
  {
    icon: Inbox,
    title: "Email Classification Agent",
    body:
      "Categorizes messages by type and intent, applies priority scoring, routes to the right rep, and drafts responses automatically.",
  },
  {
    icon: Send,
    title: "Smart Sending Agent",
    body:
      "Personalizes outreach at scale with optimal send times, manages follow-up sequences, and tracks engagement.",
  },
  {
    icon: CalendarClock,
    title: "Autonomous Booking Agent",
    body:
      "Around-the-clock meeting scheduling with calendar conflict resolution, time-zone intelligence, and automated confirmations.",
  },
  {
    icon: PhoneCall,
    title: "Call Follow-Up Agent",
    body:
      "Transcription, summarization, action-item extraction, CRM updates, and task creation triggered after every call.",
  },
];

const Agents = () => (
  <section data-testid="ai-agents-section" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">AI Agents</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Intelligent Automation for{" "}
          <span className="text-[#16A06A]">Every Workflow.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Four specialized AI agents that handle the heavy lifting across your
          revenue operations.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-5">
        {AGENTS.map((a, i) => {
          const Icon = a.icon;
          return (
            <article
              key={a.title}
              data-testid={`ai-agent-card-${i + 1}`}
              className="reveal group flex gap-5 bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={20} />
              </div>
              <div>
                <h3 className="text-[19px] font-semibold text-[#0A1628]">
                  {a.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-500">
                  {a.body}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-12 flex justify-center reveal">
        <PrimaryCTA testid="ai-agents-cta" />
      </div>
    </div>
  </section>
);

/* --------------------------------- Results --------------------------------- */

const RESULTS = [
  "Manual data entry handled automatically",
  "Inquiries answered without waiting for someone to be free",
  "Scheduling and reminders run on their own",
];

const Results = () => (
  <section data-testid="ai-results" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Results</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          ROI That <span className="text-[#16A06A]">Compounds.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          When you eliminate manual overhead and respond to leads in minutes
          instead of hours, the gains stack up fast.
        </p>
      </div>

      <ul className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {RESULTS.map((r, i) => (
          <li
            key={r}
            data-testid={`ai-result-${i + 1}`}
            className="reveal group flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#16A06A] text-white shadow-[0_6px_18px_rgba(22,160,106,0.30)]">
              <Award size={18} strokeWidth={2.5} />
            </span>
            <span className="text-[16px] lg:text-[17px] font-semibold text-[#0A1628] leading-snug">
              {r}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ------------------------------ Integrations ------------------------------ */

const INTEGRATIONS = [
  "Slack",
  "HubSpot",
  "Salesforce",
  "Gmail",
  "Google Calendar",
  "Outlook",
  "Make",
  "n8n",
  "Zapier",
  "Instantly",
  "Clay",
  "Gong",
  "HeyReach",
  "Trello",
  "Webhooks",
];

const Integrations = () => {
  const row = (offset) => [...INTEGRATIONS, ...INTEGRATIONS, ...INTEGRATIONS].map((name, i) => (
    <span
      key={`${name}-${offset}-${i}`}
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-white border border-slate-200 px-5 py-3 text-[15px] font-semibold text-slate-500 hover:text-[#0A1628] hover:border-[#16A06A]/40 transition-colors card-soft"
    >
      <span className="h-2 w-2 rounded-full bg-[#16A06A]" />
      {name}
    </span>
  ));

  return (
    <section data-testid="ai-integrations" className="py-24 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="max-w-3xl reveal">
          <Eyebrow tone="muted">Integrations</Eyebrow>
          <h2 className="h2-section mt-3 text-[#0A1628]">Works With Your Stack.</h2>
          <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
            We plug into the tools your team already runs on.
          </p>
        </div>

        <div className="mt-12 space-y-4 marquee-mask">
          <div className="overflow-hidden">
            <div className="marquee-track flex items-center gap-4">{row("a")}</div>
          </div>
          <div className="overflow-hidden">
            <div
              className="marquee-track flex items-center gap-4"
              style={{ animationDirection: "reverse", animationDuration: "45s" }}
            >
              {row("b")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA -------------------------------- */

const FinalCTA = () => (
  <section data-testid="ai-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Stop the leak</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Ready to Stop Losing Deals to{" "}
          <span className="text-[#34D399]">Slow Follow-Ups?</span>
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a 30-minute strategy call. We'll map exactly where automation
          can unlock revenue for your team.
        </p>
        <div className="mt-9 flex justify-center">
          <PrimaryCTA testid="ai-final-cta-btn" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function AiAgents() {
  useReveal();

  useEffect(() => {
    document.title = "AI Agents & Automations | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "AI agents and automations that eliminate RevOps busywork, accelerate lead response, and run around the clock. Book a free strategy call with RevGen."
      );
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div data-testid="ai-agents-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Stats />
        <Problem />
        <Solution />
        <Agents />
        <Results />
        <Integrations />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
